/* eslint-disable import/prefer-default-export */

export const FILTERS = {
  AUTHORS: 'authors',
  BOOK_TITLE: 'book_title',
  BOOK_FULL_TITLE: 'book_full_title',
  COUNTRY: 'country',
  COURSE: 'course',
  DIET_TYPE: 'diet_type',
  ERA: 'era',
  SKILL: 'skill',
};

export const FIELDS = [
  'additional_keywords',
  'authors^15',
  'authors.synonym^15',
  'book_authors^12',
  'book_authors.synonym^12',
  'book_full_title^2',
  'book_full_title.synonym^2',
  'book_licensor',
  'book_licensor.synonym',
  'book_subtitle',
  'book_subtitle.synonym',
  'book_tags',
  'book_tags.synonym',
  'book_title^2',
  'book_title.synonym^2',
  'country^2',
  'country.synonym^2',
  'course',
  'course.synonym',
  'diet_type^2',
  'diet_type.synonym^2',
  'headings',
  'headings.synonym',
  'ingredients.exact',
  'ingredients',
  'ingredients.synonym',
  'name^10',
  'skill',
  'skill.synonym',
  'subtitle.exact',
  'subtitle^10',
  'subtitle.synonym^10',
  'title.exact',
  'title^30',
  'title.synonym^20',
];

export const OPERATOR = 'and';
export const MIN_SEARCH_LENGTH = 2;

export const NUTRITION_DIET_PREFER_BOOST = 8;
export const NUTRITION_DIET_PREFER_WEIGHT = 1.5;
export const CULINARY_PREFER_BOOST = 4;
export const CULINARY_PREFER_WEIGHT = 2;
export const CULINARY_PREFER_CLAUSE_NAME = 'culinary_prefer';
export const CULINARY_REGION_PREFER_CLAUSE_NAME = 'culinary_region_prefer';

// The cross-tier contract for the diet "Prefer" setting: SearchProvider emits exactly this
// clause, and the search proxy recognises it, strips it, and re-applies the preference as a
// multiplicative function_score weight. Both sides must agree on this predicate.
export const isDietPreferClause = part =>
  Array.isArray(part?.terms?.['diet_type.raw']) && typeof part.terms.boost === 'number';

// Same contract for the Culinary Interests "Prefer" settings (topics and regions). The clause is
// a named multi_match, so it cannot be confused with the diet terms clause.
export const isCulinaryPreferClause = part =>
  part?.multi_match?._name === CULINARY_PREFER_CLAUSE_NAME &&
  typeof part.multi_match.boost === 'number';

// Region preferences are exact-term matches on continent.raw / country.raw (never free text),
// carried in a named bool so the proxy can tell it apart from the topic multi_match.
export const isCulinaryRegionPreferClause = part =>
  part?.bool?._name === CULINARY_REGION_PREFER_CLAUSE_NAME && typeof part.bool.boost === 'number';

// One label per index, hardcoded by the reindex formatters.
export const CONTENT_LABELS = ['book', 'recipe', 'person', 'collection', 'reference'];

// Which content types index each filter field, and can therefore answer a filter on it.
// An ElasticSearch term filter on a field a document never indexed EXCLUDES that document
// rather than ignoring it, and one post_filter is shared across a fan-out over five indexes
// with very different field sets. So a filter applied outside its own tiers silently empties
// whole sections: picking a Course today removes every book, author, collection and reference.
// Coverage measured on the demo index, 2026-08-25 (docs carrying the field / docs in tier):
//   country  recipe 154601, book 2856, collection 0, person 0, reference 0
//   course   recipe 186367, book    0, collection 0, person 0, reference 0
//   era      recipe 188162, book 4955, collection 0, person 0, reference 0
//   skill    recipe 186988, book    0, collection 0, person 0, reference 0
//   diet_type recipe 166562, book 176 - the one product override: 176 books carry a
//     publisher-level diet flag, but a diet narrows recipes only, so the book a surviving
//     recipe came from stays visible.
// Absent on purpose: authors, book_title and book_full_title scope the whole result set
// rather than one tier of it - "search in this book" must not leak other content back in.
export const FILTER_FIELD_TIERS = {
  country: ['recipe', 'book'],
  course: ['recipe'],
  diet_type: ['recipe'],
  era: ['recipe', 'book'],
  skill: ['recipe'],
};

// The complement is derived, never hand-written: enumerating it by hand is what dropped 'book'
// and caused this bug. Deriving it also fails closed - a content type added to CONTENT_LABELS
// but not to a field's tiers is over-filtered and visibly wrong, rather than quietly admitted
// to a filter it cannot answer.
export const exemptLabelsFor = tiers => CONTENT_LABELS.filter(label => !tiers.includes(label));

// Which filters a reader can only have chosen for themselves. SearchFilters.jsx is the sole
// producer of skill and era: category pills emit country, course and diet_type only
// (api/helpers/contentCategoryFilter.js) and no curated sample search carries either. A clause on
// one of these is therefore the reader's by construction, which the search proxy can decide alone
// - for every client, including shipped mobile binaries that cannot send an ownership signal.
export const READER_EXCLUSIVE_FILTERS = ['skill', 'era'];

// The app applies diet_type from the saved preference, and pills and curated examples emit country
// and course. Provenance for these is knowable only where the navigation happened, so the client
// has to name the ones the reader chose.
export const SHARED_FILTERS = ['country', 'course', 'diet_type'];

export const TIER_SCOPED_FILTERS = Object.keys(FILTER_FIELD_TIERS);

// An internal marker on the query object, carried from the query processor to the transport and
// stripped there. It must never reach ElasticSearch: an unrecognised top-level body key survives
// into every leg of the msearch and answers with a parsing_exception, which would fail every
// search on the site.
export const OWNED_FILTERS_QUERY_KEY = 'owned_filters';
export const OWNED_FILTERS_PARAM = 'owned';

// The tier-scoped controls the reader has operated on this search. Held on the history entry, so
// it survives a reload and back/forward but is not inherited by whoever they paste the link to.
export const TOUCHED_FILTERS_STATE_KEY = 'touchedFilters';

export const SEARCH_IMPORTANCE_BOOST_MODE = 'sum';
export const SEARCH_POPULARITY_BOOST_FACTOR = 25;
export const SEARCH_IMAGE_BOOST_WEIGHT = 10;

export const COLLECTION_IMPORTANCE_MAX = 5;

export const SORT_OPTIONS = [
  {
    label: 'Relevance',
    field: '_score',
    key: 'relevance',
    order: 'desc',
    defaultOption: true,
  },
  {
    label: 'Popularity',
    key: 'popularity',
    fields: [
      { field: 'rank', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Recently added',
    key: 'recentlyadded',
    fields: [
      { field: 'date_added', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Publication (new)',
    key: 'publication_new',
    fields: [
      { field: 'date_published', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Publication (old)',
    key: 'publication_old',
    fields: [
      { field: 'date_published', options: { order: 'asc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Highest rated',
    key: 'highest_rated',
    fields: [
      { field: 'star_rating_average', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Most ratings',
    key: 'most_ratings',
    fields: [
      { field: 'star_rating_count', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Most reviews',
    key: 'most_reviews',
    fields: [
      { field: 'review_count', options: { order: 'desc' } },
      { field: '_score', options: { order: 'desc' } },
    ],
  },
  {
    label: 'Book order',
    key: 'book',
    fields: [{ field: 'id', options: { order: 'asc' } }],
  },
];

export const INTERCOM_SEARCH_HELP_ARTICLE_ID = 2376673;

export const SEARCH_INTENT_STATE = { searchIntent: true };

export const SAMPLE_SEARCH_PARAM = 'eg';
